// WS-B — Vendor Engagement & Compensation Schedule, a bespoke staff
// compensation editor for the account-first collection composer. The
// editor assembles a single `{{CompensationSchedule}}` markdown block from
// EITHER a Tiered table (variable rows) OR a Custom free-text arrangement,
// and persists both the assembled block and the structured values into the
// item's saved JSON.
//
// This module is the PURE assembler (no React) so it can be unit-pinned.
// The formatters here MUST match the backend CollectionLetterRenderer
// (Infrastructure/Services/Collection/CollectionLetterRenderer.cs): currency
// is `$#,##0.00` (grouped, always 2 decimals) and percentage is `0.####`
// (up to 4 decimals, trailing zeros trimmed) + `%`. The shared
// `formatCurrency` in `@/design-system/lib/format` defaults to WHOLE dollars
// (maximumFractionDigits 0), so it does NOT fit — these local formatters
// reproduce the renderer's exact output instead.
//
// Phoenix Capital is the Loan Administrator / Vendor Management Program
// issuer — never a broker. No broker language appears in any assembled copy.

export interface CompensationTier {
  /** Upper bound of this tier ("up to $X per period"). Null marks the
   *  open-ended top tier ("Above $<previous> per period"). */
  threshold: number | null;
  rateType: "percent" | "flat";
  /** Percent rate when rateType === "percent" (e.g. 7.5 → "7.5%"). */
  ratePct: number | null;
  /** Flat per-period amount when rateType === "flat". */
  rateFlat: number | null;
}

export interface CompensationValues {
  compensationMethod: "tiered" | "custom";
  tiers: CompensationTier[];
  customCompensation: string;
}

/** `$#,##0.00` — grouped dollars, always two decimals (backend parity). */
export function formatCompensationCurrency(amount: number | null): string {
  if (amount === null || Number.isNaN(amount)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** `0.####` + `%` — up to four decimals, trailing zeros trimmed (backend
 *  parity, e.g. 7.5 → "7.5%", 5 → "5%"). */
export function formatCompensationPercent(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "";
  const trimmed = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
    useGrouping: false,
  }).format(value);
  return `${trimmed}%`;
}

/** Render a single tier's rate phrase ("<ratePct>%" or "$<rateFlat>"). */
function formatRate(tier: CompensationTier): string {
  return tier.rateType === "percent"
    ? formatCompensationPercent(tier.ratePct)
    : formatCompensationCurrency(tier.rateFlat);
}

/**
 * Sort tiers ascending by threshold, nulls last (the open-ended top tier
 * always sorts to the end). Stable enough for the editor's needs — equal
 * thresholds keep input order.
 */
function sortTiers(tiers: CompensationTier[]): CompensationTier[] {
  return tiers.slice().sort((a, b) => {
    if (a.threshold === null && b.threshold === null) return 0;
    if (a.threshold === null) return 1;
    if (b.threshold === null) return -1;
    return a.threshold - b.threshold;
  });
}

const WHOLE_AMOUNT_RULE =
  "*Whole-amount application: once the volume for a Measurement Period " +
  "reaches a tier's threshold, that tier's single rate applies to the " +
  "entire volume for that period (not only the amount above the threshold). " +
  "The rate increases with volume as set above.*";

/**
 * Assemble the `{{CompensationSchedule}}` markdown block. Bold-label LINES,
 * each its own paragraph separated by a BLANK line (the pre-approval letter
 * convention — Pandoc renders each as its own paragraph).
 */
export function assembleCompensationSchedule(values: CompensationValues): string {
  if (values.compensationMethod === "custom") {
    return values.customCompensation.trim();
  }

  const tiers = sortTiers(values.tiers);
  if (tiers.length === 0) return "";

  const lines: string[] = [];
  if (tiers.length === 1) {
    lines.push(
      `**Tier 1:** Applies to all volume per Measurement Period — ${formatRate(tiers[0])}`,
    );
  } else {
    tiers.forEach((tier, i) => {
      const n = i + 1;
      const rate = formatRate(tier);
      if (i === 0) {
        lines.push(
          `**Tier ${n}:** Up to ${formatCompensationCurrency(tier.threshold)} per period — ${rate}`,
        );
      } else if (i === tiers.length - 1) {
        const prev = formatCompensationCurrency(tiers[i - 1].threshold);
        lines.push(`**Tier ${n}:** Above ${prev} per period — ${rate}`);
      } else {
        const prev = formatCompensationCurrency(tiers[i - 1].threshold);
        const upper = formatCompensationCurrency(tier.threshold);
        lines.push(`**Tier ${n}:** Above ${prev}, up to ${upper} per period — ${rate}`);
      }
    });
  }

  // Bold-label lines + a blank line + the italic whole-amount rule, each its
  // own paragraph (blank-line separated) so Pandoc keeps them distinct.
  return [...lines, WHOLE_AMOUNT_RULE].join("\n\n");
}

/**
 * Serialize the structured values plus the assembled block as the JSON that
 * lands in the item's SavedFormDataJson. `CompensationSchedule` is the merge
 * token the template body substitutes; the structured keys round-trip the
 * editor state.
 */
export function serializeCompensation(values: CompensationValues): string {
  return JSON.stringify({
    compensationMethod: values.compensationMethod,
    tiers: values.tiers,
    customCompensation: values.customCompensation,
    CompensationSchedule: assembleCompensationSchedule(values),
  });
}

/** A fresh open-ended top tier (no threshold). The editor seeds one of
 *  these as the default single tier. */
export function makeOpenTier(): CompensationTier {
  return { threshold: null, rateType: "percent", ratePct: null, rateFlat: null };
}

/** The editor's default state — Tiered with one open-ended tier. */
export function defaultCompensationValues(): CompensationValues {
  return { compensationMethod: "tiered", tiers: [makeOpenTier()], customCompensation: "" };
}
